import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import { FeatureFlagAuditEntryDtoIn } from '@/models/in/FeatureFlagDtoIn';
import HoverHint from '@/components/ui/Tooltip/HoverHint';
import { relativeTime } from '@/utils/relativeTime';
import useNameMaps from './useNameMaps';
import { NameMaps, classificationName, nameList, roleName, statusName } from './targetingNames';
import styles from './FeatureFlagHistory.module.scss';

type Snapshot = Record<string, unknown>;

const parse = (json?: string): Snapshot | null => {
  if (!json) return null;
  try {
    const value = JSON.parse(json);
    return value && typeof value === 'object' ? (value as Snapshot) : null;
  } catch {
    return null;
  }
};

const list = (snapshot: Snapshot | null, key: string): unknown[] => {
  const value = snapshot?.[key];
  return Array.isArray(value) ? value : [];
};

/**
 * What one audit entry changed, in words: «אתר: סגור ← פתוח לחלק», «נוספו פרויקטים: קופה ראשית».
 * The server records a before and an after snapshot; this reads the difference. An entry it does
 * not know keeps the server's own words.
 */
export const describeEntry = (entry: FeatureFlagAuditEntryDtoIn, maps: NameMaps, t: TFunction): string[] => {
  const before = parse(entry.stateBefore);
  const after = parse(entry.stateAfter);
  const k = (key: string, options?: Record<string, unknown>) => t(`featureFlags.historyView.${key}`, options);

  switch (entry.content) {
    case 'Finalized release':
      return [k('finalized')];
    case 'Returned to the release list':
      return [k('unfinalized')];
    case 'Cancelled feature':
      return [k('retired')];
    case 'Restored cancelled feature':
      return [k('unretired')];
    case 'Updated flag settings': {
      const lines: string[] = [];
      const status = (key: string, label: string) => {
        const from = statusName(before?.[key]);
        const to = statusName(after?.[key]);
        if (from && to && from !== to) lines.push(k('statusChange', { where: label, from: t(`featureFlags.status.${from}`), to: t(`featureFlags.status.${to}`) }));
      };
      status('LifecycleStatus', k('site'));
      status('NedarimLifecycleStatus', k('nedarim'));
      if (before?.Description !== after?.Description) lines.push(k('description'));
      if (before?.DisplayName !== after?.DisplayName) lines.push(k('name'));
      return lines.length > 0 ? lines : [k('saved')];
    }
    case 'Replaced targeting': {
      const lines: string[] = [];
      const diff = (key: string, kind: string, label: (v: unknown) => string) => {
        const was = list(before, key).map(String);
        const now = list(after, key).map(String);
        const added = list(after, key).filter((v) => !was.includes(String(v)));
        const removed = list(before, key).filter((v) => !now.includes(String(v)));
        const kindName = t(`featureFlags.dialog.picker.kinds.${kind}`);
        if (added.length > 0) lines.push(k('added', { kind: kindName, names: nameList(added.map(label), t, 3) }));
        if (removed.length > 0) lines.push(k('removed', { kind: kindName, names: nameList(removed.map(label), t, 3) }));
      };
      diff('FundIds', 'funds', (v) => maps.fundName(Number(v)));
      diff('GroupIds', 'groups', (v) => maps.groupName(Number(v)));
      diff('UserEmails', 'users', (v) => maps.userName(String(v)));
      diff('Classifications', 'classifications', (v) => t(`featureFlags.classificationLabels.${classificationName(v)}`, classificationName(v)));
      diff('Roles', 'roles', (v) => t(`featureFlags.roleLabels.${roleName(v)}`, roleName(v)));
      return lines.length > 0 ? lines : [k('targetingSaved')];
    }
    default:
      return [entry.content];
  }
};

/**
 * A flag's history, inside its release dialog (HAT-1489 rework): who changed what and when, in
 * Hebrew, newest first - instead of a second window with the server's English and raw JSON.
 */
const FeatureFlagHistory = ({ featureKey }: { featureKey: string }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'en' ? 'en-GB' : 'he-IL';
  const { data: entries, isLoading } = useGetAuthApi<FeatureFlagAuditEntryDtoIn[]>(`management/feature-flags/${featureKey}/audit`);
  const { maps, isLoading: namesLoading } = useNameMaps();

  if (isLoading || namesLoading) return <p className="body-sm-regular text-(--font-secondary)">{t('loading')}</p>;
  if ((entries?.length ?? 0) === 0) return <p className="body-sm-regular text-(--font-secondary)">{t('featureFlags.history_empty')}</p>;

  const ordered = [...(entries ?? [])].sort((a, b) => b.dateModified.localeCompare(a.dateModified));
  return (
    <ol className={styles.list}>
      {ordered.map((entry, index) => (
        <li key={`${entry.dateModified}-${index}`} className={styles.entry}>
          <HoverHint as="div" className={styles.when} hint={new Date(entry.dateModified).toLocaleString(locale)}>
            <span className="body-sm-semibold">{relativeTime(entry.dateModified, t, locale)}</span>
            <span className="body-xs-regular text-(--font-body-3)">{new Date(entry.dateModified).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}</span>
          </HoverHint>
          <div className={styles.what}>
            {describeEntry(entry, maps, t).map((line) => (
              <span key={line} className="body-sm-regular">{line}</span>
            ))}
          </div>
          <span className={`${styles.who} body-sm-regular`}>{maps.userName(entry.modifiedBy)}</span>
        </li>
      ))}
    </ol>
  );
};

export default FeatureFlagHistory;
